import React from "react";
import { Zap } from "lucide-react";
import { goToCheckout } from "../lib/checkout";

/**
 * Single source of truth for every ₹199 registration CTA.
 * - Forwards UTM parameters
 * - Fires standardised tracking events
 * - Never a dead button
 */
export default function CTAButton({
  children = "RESERVE MY SEAT FOR ₹199",
  source = "generic",
  size = "md",
  variant = "primary",
  className = "",
  showIcon = true,
  showArrow = true,
  block = false,
}) {
  const sizes = {
    sm: "px-4 py-2.5 text-xs md:text-sm rounded-xl",
    md: "px-6 py-3.5 text-sm md:text-base rounded-2xl",
    lg: "px-7 py-4 text-sm md:text-base rounded-2xl",
    xl: "px-8 py-5 text-base md:text-lg rounded-2xl",
  };

  const isPrimary = variant === "primary";
  const isGhost = variant === "ghost";
  const isDark = variant === "dark";

  const base =
    "group relative inline-flex items-center justify-center gap-2.5 font-extrabold tracking-wide cursor-pointer select-none overflow-hidden transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C4FF3D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f0a]";

  const variantClasses = isPrimary
    ? // Premium primary: layered gradient, refined shadow, inner highlight
      "cta-pulse text-[#0a0f0a] hover:shadow-[0_16px_40px_-8px_rgba(196,255,61,0.8),0_0_0_1px_rgba(255,255,255,0.18)_inset,0_1px_0_rgba(255,255,255,0.4)_inset] hover:-translate-y-0.5 active:translate-y-0"
    : isGhost
    ? "border border-[rgba(196,255,61,0.4)] text-white hover:border-[#C4FF3D] hover:text-[#C4FF3D] bg-transparent hover:bg-[rgba(196,255,61,0.05)]"
    : isDark
    ? "bg-[#0e1410] border border-[rgba(196,255,61,0.35)] text-white hover:border-[#C4FF3D] hover:bg-[#111a12]"
    : "";

  return (
    <button
      type="button"
      onClick={() => goToCheckout(source)}
      className={`${base} ${sizes[size]} ${variantClasses} ${block ? "w-full" : ""} ${className}`}
      aria-label={typeof children === "string" ? children : "Reserve my seat"}
      style={
        isPrimary
          ? {
              backgroundImage:
                "linear-gradient(180deg, #D3FF5C 0%, #C4FF3D 45%, #A8E82A 100%)",
            }
          : undefined
      }
    >
      {/* Inner top highlight — the subtle glass sheen that makes it feel premium */}
      {isPrimary && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-3 top-0 h-1/2 rounded-t-2xl opacity-70"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 100%)",
          }}
        />
      )}

      {/* Animated sheen sweep */}
      {isPrimary && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] ease-out"
          style={{
            backgroundImage:
              "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)",
          }}
        />
      )}

      {/* Content */}
      {showIcon && isPrimary && (
        <Zap className="relative w-4 h-4 md:w-[18px] md:h-[18px] shrink-0" fill="currentColor" />
      )}
      <span className="relative text-center leading-tight">{children}</span>
      {showArrow && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-4 h-4 md:w-[18px] md:h-[18px] shrink-0 transition-transform duration-200 group-hover:translate-x-1"
          aria-hidden="true"
        >
          <path d="M5 12h14" />
          <path d="m13 5 7 7-7 7" />
        </svg>
      )}
    </button>
  );
}
